import {
  QueryClient,
  QueryClientProvider,
  QueryClientProviderProps,
} from "@tanstack/react-query";
import React, { useRef } from "react";

import { createMainStore, MainStoreContext } from "@/stores/main";
import { createMappingStore, MappingStoreContext } from "@/stores/mapping";
import { createPreviewStore, PreviewStoreContext } from "@/stores/preview";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 0,
      staleTime: 0,
      retry: false,
      throwOnError: false,
    },
  },
});

const MainStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<ReturnType<typeof createMainStore>>();
  if (!storeRef.current) storeRef.current = createMainStore();

  return (
    <MainStoreContext.Provider value={storeRef.current}>
      {children}
    </MainStoreContext.Provider>
  );
};

const MappingStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<ReturnType<typeof createMappingStore>>();
  if (!storeRef.current) storeRef.current = createMappingStore();

  return (
    <MappingStoreContext.Provider value={storeRef.current}>
      {children}
    </MappingStoreContext.Provider>
  );
};

const PreviewStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<ReturnType<typeof createPreviewStore>>();
  if (!storeRef.current) storeRef.current = createPreviewStore();

  return (
    <PreviewStoreContext.Provider value={storeRef.current}>
      {children}
    </PreviewStoreContext.Provider>
  );
};

/**
 * 모달이 구독해도 되는 store 의 provider 묶음. **ModalStack 은 반드시 이 안에서 렌더한다** —
 * 스택은 로그인·약관 게이트 화면에서도 떠야 해서 AuthWrapper 바깥에 있고, 그래서 모달이
 * 볼 수 있는 provider 는 여기 있는 것이 전부다. 밖으로 나가면 해당 store 를 쓰는 모달이
 * 열리는 순간 throw 한다 (실제로 AddTagModal·DragTagModal 등으로 두 번 터졌다).
 *
 * 여기 없는 것: AuthProvider(firebase 구독이라 게이트 안쪽), 그리고 라우트 단위 store
 * (EditSessionStore, IxdEditorStore, IxdDsdCompareStore …). 모달이 그 값을 써야 하면
 * push 하는 쪽에서 props 로 넘긴다 — AddTagModal 의 workbookId/tableId 가 그 예다.
 *
 * 계약은 providers.test.tsx 가 고정한다.
 */
export const AppProviders = ({
  children,
  client = queryClient,
}: {
  children: React.ReactNode;
  client?: QueryClientProviderProps["client"];
}) => (
  <QueryClientProvider client={client}>
    <MainStoreProvider>
      <MappingStoreProvider>
        <PreviewStoreProvider>{children}</PreviewStoreProvider>
      </MappingStoreProvider>
    </MainStoreProvider>
  </QueryClientProvider>
);
